import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Box,
	Grid,
	Divider,
	IconButton,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import { useParams, useSearchParams } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { QualityType } from '@/api/enum/common.enum';
import { BlindBoxDetailDTO } from '@/api/DTO/exchange/box.interface';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import ChangeIntegralModal from './ChangeIntegralModal';
import { showLabel } from '@/utils/helper';
import PublishedChip from '@/common/components/PublishedChip';
import { BOX_SERIES_TYPE, PUBLISHED_LISTB } from '@/common/constants/common';
import dayjs from 'dayjs';
import { DISPLAY_DATETIME_12H_FORMAT } from '@/utils/dateHelper';
import {
	UserTreasureFilter,
	UserTreasureRecordDetailDTO,
} from '@/api/DTO/user/userList.interface';
import {
	useDeleteUserTreasureMutation,
	useGetUserPointsQuery,
	useGetUserTreasureQuery,
} from '@/api/user/userApiSlice';
import AddTreasureDialog from './AddTreasureDialog';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import ChangeTreasureTotalModal from './ChangeTreasureTotalModal';

export interface DataGrid {
	updatedAt?: string;
	createdAt?: string;
	publishedStartAt?: string;
	publishedEndAt?: string;
	id?: string;
	published?: number | string | boolean;
}

interface Props {
	details?: BlindBoxDetailDTO;
}

export default function UserTreasureSummaryPage({ details }: Props) {
	const showModal = useModal();
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const { id } = useParams();
	const [deleteUserTreasure] = useDeleteUserTreasureMutation();

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultPropertyFilter: UserTreasureFilter = {
		pagination: initialPagination,
		sortings: [],
		isHad: 1,
		userId: id,
	};

	const defaultValues: DefaultValues<UserTreasureFilter> =
		defaultPropertyFilter;
	const methods = useForm<UserTreasureFilter>({
		defaultValues,
	});

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as UserTreasureFilter;

			// append to url to reload page
			setSearchParams(qs.stringify(propertyFilterFormResult));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const transformedSearchParams: UserTreasureFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const {
		data: propertySummary,
		isSuccess: isGetPropertySummarySuccess,
		refetch,
	} = useGetUserTreasureQuery(transformedSearchParams, {
		refetchOnMountOrArgChange: true,
	});

	const { data: userPoints } = useGetUserPointsQuery(id || '', {
		refetchOnMountOrArgChange: true,
	});

	const transformResponse = useCallback(
		(response?: UserTreasureRecordDetailDTO[]) => {
			if (response == null) return;
			const newResponse: DataGrid[] = [];
			response.forEach((item) => {
				newResponse.push({
					...item,
					publishedStartAt: !item.publishedStartAt
						? '-'
						: dayjs(item.publishedStartAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
					publishedEndAt: !item.publishedEndAt
						? '-'
						: dayjs(item.publishedEndAt)
								.locale('en')
								.format(DISPLAY_DATETIME_12H_FORMAT),
				});
			});
			return newResponse;
		},
		[]
	); // watch fields change

	const handleDelete = (row: any) => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('user.deleteTreasure')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('user.isDeleteTreasure')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.isConfirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteUserTreasure({
						userId: id || '',
						treasureId: row.gameTreasureId,
					});
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteTreasureSuccess'), {
							variant: 'success',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						refetch();
					}
				}}
			/>
		));
	};

	const columns: GridColDef[] = [
		{
			field: 'gameType',
			headerName: t('box.typeName'),
			flex: 21,
			renderCell: (params: any) =>
				showLabel(params.row.gameType, BOX_SERIES_TYPE),
		},
		{
			field: 'gameSeriesName',
			headerName: t('box.seriesName'),
			flex: 21,
		},
		{
			field: 'gameTreasureName',
			headerName: t('box.treasureName'),
			flex: 21,
		},
		{
			field: 'quality',
			headerName: t('chartletList.quality'),
			flex: 10,
			renderCell: (params: any) => showLabel(params.row.quality, qualityList),
		},
		{
			field: 'userTreasureCount',
			headerName: t('user.userTreasureTotal'),
			flex: 10,
		},

		{
			field: 'published',
			headerName: t('box.treasureStatus'),
			flex: 10,
			renderCell: (params: any) => (
				<PublishedChip
					published={params.row.published}
					publishList={PUBLISHED_LISTB}
				/>
			),
		},
		{
			field: 'publishedStartAt',
			headerName: t('achievementList.pushedTime'),
			flex: 10,
		},
		{
			field: 'publishedEndAt',
			headerName: t('achievementList.pulledTime'),
			flex: 10,
		},
		{
			field: 'edit',
			headerName: '',
			width: 100,
			renderCell: (params) => (
				<>
					<IconButton color='error' onClick={() => handleDelete(params.row)}>
						<DeleteOutlineOutlinedIcon />
					</IconButton>
					<ChangeTreasureTotalModal details={params.row} />
				</>
			),
		},
	];

	return (
		<Card sx={{ marginTop: 3 }}>
			<CardContent>
				<Box display='flex' alignItems='center'>
					<Typography variant='h6' marginRight={4}>
						{t('user.myIntegral')}:{userPoints?.data}
					</Typography>
					<ChangeIntegralModal rewardPoints={userPoints?.data || 0} />
				</Box>
				<Divider sx={{ margin: '20px 0' }} />
				<FormProvider {...methods}>
					<Grid
						container
						rowSpacing={2}
						columnSpacing={1}
						alignItems='center'
						justifyContent='center'
					>
						<Grid item xs={9}>
							<Typography variant='h6'>{t('user.myTreasure')}</Typography>
						</Grid>
						<Grid item xs={2}>
							<AppSummarySearchBar
								name='searchString'
								control={methods.control}
								placeholder={t('common.search')}
							/>
						</Grid>
						<Grid
							item
							xs={1}
							container
							justifyContent='flex-end'
							sx={{ marginLeft: 'auto' }}
						>
							<AddTreasureDialog />
						</Grid>
					</Grid>
					<Box marginTop={2}>
						<AppDataGrid
							loading={!isGetPropertySummarySuccess}
							rows={transformResponse(propertySummary?.data?.items) ?? []}
							rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
							columns={columns}
							getRowId={(row) => row.gameTreasureId}
						/>
					</Box>
				</FormProvider>
			</CardContent>
		</Card>
	);
}
